import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, X, ImagePlus, Loader2, Wand2, Rocket, Music2, Sparkles,
  Zap, Trophy, Coins, Gauge, Star, Lock, TriangleAlert, ArrowUpRight,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

const SCENE_LABEL = {
  living_room: "Living room", kitchen: "Kitchen", bathroom: "Bathroom",
  bedroom: "Bedroom", terrace: "Terrace", pool: "Pool", garden: "Garden",
  exterior: "Exterior", view: "View", other: "Other",
};

const TIER_ICONS = { fastest: Zap, cheapest: Coins, best_value: Star, best_quality: Trophy };

/* Simplified tier tile — Budget / Premium / Ultra */
function TierCard({ id, label, stars, credits, providersHint, description, selected, onSelect, highlight }) {
  return (
    <button
      onClick={onSelect}
      data-testid={`tier-${id}`}
      className={`relative p-5 rounded-lg border text-left transition-colors ${
        selected
          ? "border-amber-500 bg-gradient-to-br from-amber-500/15 to-amber-500/5"
          : highlight
            ? "border-amber-500/30 bg-[#121214] hover:border-amber-500/60"
            : "border-white/5 bg-[#121214] hover:border-white/15"
      }`}
    >
      {highlight && !selected && (
        <div className="absolute top-2 right-2 font-mono-tech text-[8px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 uppercase">
          Popular
        </div>
      )}
      <div className="flex items-center gap-2 mb-2">
        <div className="font-heading font-bold text-base tracking-tight">{label}</div>
        <span className="text-[10px] text-amber-400">{stars}</span>
      </div>
      <div className="text-xs text-zinc-400 mb-3 min-h-[2.5em]">{description}</div>
      <div className="flex items-center gap-1 text-[11px] text-zinc-400 font-mono-tech mb-2">
        <Coins className="w-3 h-3 text-amber-400" strokeWidth={1.5} />
        {credits} credits/clip
      </div>
      {providersHint && (
        <div className="text-[9px] font-mono-tech text-zinc-500 uppercase tracking-wide truncate" title={providersHint}>
          {providersHint}
        </div>
      )}
    </button>
  );
}

export default function CreateProject() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const editProjectId = searchParams.get("edit");
  const fileRef = useRef(null);
  const { balance, refreshBalance, isOwner } = useAuth();

  // Single-page flow: `activeSection` only drives nav highlighting (setup/photos/style).
  // All sections are always rendered; progressive disclosure handles prerequisites.
  const [activeSection, setActiveSection] = useState("setup");
  const [project, setProject] = useState(null);
  const setupRef = useRef(null);
  const photosRef = useRef(null);
  const styleRef = useRef(null);
  // While true, IntersectionObserver updates are ignored so the pill the user
  // just clicked stays highlighted through the scroll animation.
  const scrollLockUntilRef = useRef(0);
  const scrollToSection = (id) => {
    const map = { setup: setupRef, photos: photosRef, style: styleRef };
    const el = map[id]?.current;
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(id);
      scrollLockUntilRef.current = Date.now() + 700;
    }
  };
  // IntersectionObserver highlights the section closest to viewport top.
  // rootMargin creates a narrow "active band" ~120px below sticky nav so the
  // section whose heading is just below the nav is the highlighted one.
  useEffect(() => {
    const els = [
      { id: "setup", el: setupRef.current },
      { id: "photos", el: photosRef.current },
      { id: "style", el: styleRef.current },
    ].filter((x) => x.el);
    if (!els.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        // Respect the scroll-lock so click-triggered scrolls don't get overridden.
        if (Date.now() < scrollLockUntilRef.current) return;
        // Find the section whose top edge is closest to 120px from viewport top
        // AND still visible on screen. This is the most user-intuitive rule.
        const candidates = entries
          .filter((e) => e.isIntersecting)
          .map((e) => ({ e, id: els.find((x) => x.el === e.target)?.id }))
          .filter((x) => x.id);
        if (!candidates.length) return;
        // Prefer the section whose top is nearest 120px (but not above).
        candidates.sort((a, b) => {
          const at = a.e.boundingClientRect.top;
          const bt = b.e.boundingClientRect.top;
          // Penalise negative (section already scrolled past)
          const aScore = at < 0 ? Math.abs(at) + 5000 : Math.abs(at - 120);
          const bScore = bt < 0 ? Math.abs(bt) + 5000 : Math.abs(bt - 120);
          return aScore - bScore;
        });
        setActiveSection(candidates[0].id);
      },
      { rootMargin: "0px 0px -50% 0px", threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] }
    );
    els.forEach((x) => obs.observe(x.el));
    return () => obs.disconnect();
  }, [project]);
  const [catalog, setCatalog] = useState(null);
  const [providers, setProviders] = useState([]);
  const [tierInfo, setTierInfo] = useState(null);
  const [advancedMode, setAdvancedMode] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [description, setDescription] = useState("");

  const [providerId, setProviderId] = useState("ffmpeg-preview");
  const [autoTier, setAutoTier] = useState("best_value");
  const [style, setStyle] = useState("cinematic");
  const [aspect, setAspect] = useState("16:9");
  const [resolution, setResolution] = useState("1080p");
  const [language, setLanguage] = useState("en");
  const [musicTrack, setMusicTrack] = useState("cinematic_ambient.mp3");
  const [musicVolume, setMusicVolume] = useState(0.35);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [voiceGender, setVoiceGender] = useState("female");
  const [voiceScript, setVoiceScript] = useState("");
  const [captionsEnabled, setCaptionsEnabled] = useState(true);
  const [captionText, setCaptionText] = useState("");
  const [brandingEnabled, setBrandingEnabled] = useState(true);
  const [currentPlan, setCurrentPlan] = useState(null); // {tier, plan, has_subscription}
  const [hasFalKey, setHasFalKey] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/meta/catalog").then((r) => setCatalog(r.data)),
      api.get("/providers").then((r) => setProviders(r.data)),
      api.get("/providers/tiers").then((r) => setTierInfo(r.data)).catch(() => {}),
      api.get("/music").then((r) => setTracks(r.data)),
      api.get("/templates").then((r) => setTemplates(r.data)),
      api.get("/subscriptions/current").then((r) => setCurrentPlan(r.data)).catch(() => {}),
      api.get("/providers/_meta/has_fal_key").then((r) => setHasFalKey(!!r.data?.has_fal_key)).catch(() => {}),
    ]);
  }, []);

  // Restore an existing project when the URL contains ?edit=<id>. Populates every
  // field (name / address / description / photos / style / provider / music / voice
  // / captions / branding / aspect / language) so a user returning from a failed
  // generation NEVER has to re-enter anything.
  useEffect(() => {
    if (!editProjectId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: p } = await api.get(`/projects/${editProjectId}`);
        if (cancelled || !p) return;
        setProject(p);
        setName(p.name || "");
        setAddress(p.address || "");
        setDescription(p.description || "");
        const s = p.settings || {};
        if (s.provider_id) setProviderId(s.provider_id);
        if (s.auto_tier) setAutoTier(s.auto_tier);
        if (s.style) setStyle(s.style);
        if (s.aspect) setAspect(s.aspect);
        if (s.resolution) setResolution(s.resolution);
        if (s.language) setLanguage(s.language);
        if (s.music_track !== undefined) setMusicTrack(s.music_track);
        if (typeof s.music_volume === "number") setMusicVolume(s.music_volume);
        if (typeof s.voice_enabled === "boolean") setVoiceEnabled(s.voice_enabled);
        if (s.voice_gender) setVoiceGender(s.voice_gender);
        if (s.voice_script != null) setVoiceScript(s.voice_script);
        if (typeof s.captions_enabled === "boolean") setCaptionsEnabled(s.captions_enabled);
        if (s.caption_text != null) setCaptionText(s.caption_text);
        if (typeof s.branding_enabled === "boolean") setBrandingEnabled(s.branding_enabled);
        // On a single-page flow, jump-scroll to the most relevant section so the
        // user can immediately continue from where the project last was.
        setTimeout(() => {
          if ((p.photos || []).length > 0) scrollToSection("style");
          else if (p.name) scrollToSection("photos");
        }, 100);
      } catch (err) {
        toast.error("Could not restore project — starting fresh.");
      }
    })();
    return () => { cancelled = true; };
  }, [editProjectId]);

  // Plan-driven filters
  const planAllowedProviders = currentPlan?.plan?.allowed_providers || [];
  const planAllowedResolutions = currentPlan?.plan?.allowed_resolutions || [];
  const maxLenSec = currentPlan?.plan?.max_length_seconds || 999;

  // No plan-based provider gating — every user picks freely, credits gate access.
  const isProviderAllowed = () => true;
  const isResolutionAllowed = () => true;

  // Auto-switch: nothing to do (all providers/resolutions are always allowed).
  useEffect(() => {}, [currentPlan, providers]);

  const currentSettings = () => ({
    provider_id: providerId, auto_tier: providerId === "auto" ? autoTier : null,
    style, aspect, resolution, language,
    music_track: musicTrack || null, music_volume: musicVolume,
    voice_enabled: voiceEnabled, voice_gender: voiceGender, voice_script: voiceScript,
    captions_enabled: captionsEnabled, caption_text: captionText,
    branding_enabled: brandingEnabled,
  });

  const effectiveProvider = useMemo(() => {
    if (providerId === "auto") {
      const eligible = providers.filter((p) => p.enabled && p.tier === autoTier);
      if (!eligible.length) return null;
      if (autoTier === "fastest") return eligible.sort((a, b) => b.speed_score - a.speed_score)[0];
      if (autoTier === "cheapest") return eligible.sort((a, b) => a.credits_per_clip - b.credits_per_clip)[0];
      if (autoTier === "best_quality") return eligible.sort((a, b) => b.quality_score - a.quality_score)[0];
      return eligible.sort((a, b) => b.quality_score / Math.max(1, b.credits_per_clip) - a.quality_score / Math.max(1, a.credits_per_clip))[0];
    }
    // Tier-group synthetic IDs (budget-tier / premium-tier / ultra-tier) —
    // resolve to the tier's fixed credits/clip so the cost card is accurate.
    // Backend resolves the actual provider at generate-time via _resolve_provider_id.
    const tierMatch = providerId?.match(/^(budget|premium|ultra)-tier$/);
    if (tierMatch) {
      const group = tierMatch[1];
      const t = tierInfo?.tiers?.find((x) => x.tier_group === group);
      const label = group.charAt(0).toUpperCase() + group.slice(1);
      return {
        id: providerId,
        name: `${label} tier · auto-picked`,
        credits_per_clip: t?.credits_per_clip ?? { budget: 20, premium: 35, ultra: 80 }[group],
        tier_group: group,
      };
    }
    return providers.find((p) => p.id === providerId);
  }, [providers, providerId, autoTier, tierInfo]);

  const totalCredits = (project?.photos?.length || 0) * (effectiveProvider?.credits_per_clip || 0);
  const canAfford = isOwner || balance.unlimited || balance.balance >= totalCredits;

  // Upsell: track how often the user picks a Premium/Ultra tier they can't afford.
  // Non-intrusive banner appears after 2+ unaffordable picks, prompting a Studio upgrade.
  const tierGroup = effectiveProvider?.tier_group;
  const isStudioPlus = ["studio", "enterprise"].includes((currentPlan?.tier || "").toLowerCase());
  const upsellEligible = !isOwner && !isStudioPlus && !canAfford &&
    (tierGroup === "premium" || tierGroup === "ultra");
  const [upsellCount, setUpsellCount] = useState(() => {
    try { return parseInt(localStorage.getItem("mo_upsell_count") || "0", 10) || 0; }
    catch { return 0; }
  });
  useEffect(() => {
    if (!upsellEligible) return;
    setUpsellCount((prev) => {
      const next = prev + 1;
      try { localStorage.setItem("mo_upsell_count", String(next)); } catch { /* ignore */ }
      return next;
    });
    // Trigger once per (tier + provider) selection change.
  }, [providerId, autoTier, upsellEligible]);
  const showUpsell = upsellEligible && upsellCount >= 2;

  // Plan-limit pre-flight — matches backend gating
  const numPhotos = project?.photos?.length || 0;
  const totalLenSec = numPhotos * 5;  // default duration_per_photo=5
  const planViolations = [];
  if (!isOwner && currentPlan?.plan) {
    if (totalLenSec > (currentPlan.plan.max_length_seconds || 999)) {
      planViolations.push(`Video is ${totalLenSec}s but your ${currentPlan.plan.name} plan allows up to ${currentPlan.plan.max_length_seconds}s`);
    }
    if (!isResolutionAllowed(resolution)) {
      planViolations.push(`${resolution.toUpperCase()} resolution not included in ${currentPlan.plan.name}`);
    }
    if (providerId !== "auto" && !isProviderAllowed(providerId)) {
      planViolations.push(`This provider isn't included in your ${currentPlan.plan.name} plan`);
    }
  }
  const planOk = planViolations.length === 0;

  // Auto-save the project draft. Called on name blur when the user has typed
  // a name, so the photos + style sections unlock without a manual "Continue"
  // button. Silent (no toast) unless there's an error.
  //
  // Concurrency guard: `savingRef` prevents any two concurrent createDraft()
  // calls from both POSTing (fixes duplicate-project race between the onBlur
  // handler and the debounced useEffect). Also freshly reads `project` inside
  // to defeat stale-closure captures.
  const savingRef = useRef(false);
  const projectRef = useRef(null);
  useEffect(() => { projectRef.current = project; }, [project]);
  const createDraft = async (opts = { silent: false, scrollNext: false }) => {
    if (!name.trim()) {
      if (!opts.silent) toast.error("Please give the project a name");
      return null;
    }
    if (savingRef.current) return null;
    savingRef.current = true;
    setBusy(true);
    try {
      let saved;
      const currentProject = projectRef.current;
      if (currentProject?.id) {
        const { data } = await api.patch(`/projects/${currentProject.id}`, {
          name: name.trim(), address, description, settings: currentSettings(),
        });
        saved = data;
      } else {
        const { data } = await api.post("/projects", {
          name: name.trim(), address, description, settings: currentSettings(),
        });
        saved = data;
        if (!captionText) setCaptionText(name.trim());
      }
      setProject(saved);
      projectRef.current = saved; // keep ref in sync immediately for chained saves
      if (opts.scrollNext) scrollToSection("photos");
      return saved;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save project");
      return null;
    } finally {
      setBusy(false);
      savingRef.current = false;
    }
  };

  // Debounced autosave — fires when name has been quiet for 800ms AND user
  // isn't editing an existing project (that has its own hydration flow).
  useEffect(() => {
    if (!name.trim()) return;
    if (projectRef.current?.id) return; // Only auto-create the FIRST time
    const t = setTimeout(() => {
      // Re-check ref inside the timer — an onBlur may have already created it.
      if (projectRef.current?.id || savingRef.current) return;
      createDraft({ silent: true, scrollNext: false });
    }, 800);
    return () => clearTimeout(t);
  }, [name]);

  const handleFiles = async (files) => {
    if (!files?.length || !project) return;
    if ((project.photos?.length || 0) + files.length > 30) return toast.error("Max 30 photos");
    setBusy(true);
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    try {
      const { data } = await api.post(`/projects/${project.id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProject(data);
      toast.success(`Uploaded ${files.length} photo(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = async (photoId) => {
    try {
      const { data } = await api.delete(`/projects/${project.id}/photos/${photoId}`);
      setProject(data);
    } catch {
      toast.error("Delete failed");
    }
  };

  const setScene = async (photoId, scene) => {
    const fd = new FormData();
    fd.append("scene", scene);
    const { data } = await api.patch(`/projects/${project.id}/photos/${photoId}`, fd);
    setProject(data);
  };

  const saveAndGenerate = async () => {
    if (!project?.photos?.length) return toast.error("Upload at least one photo");
    if (!planOk) return toast.error(planViolations[0]);
    if (!canAfford) return toast.error(`Not enough credits — need ${totalCredits}, you have ${isOwner || balance.unlimited ? "∞" : balance.balance}`);
    setBusy(true);
    try {
      await api.patch(`/projects/${project.id}`, { settings: currentSettings() });
      await api.post(`/projects/${project.id}/generate`);
      toast.success("Studio started rendering your video");
      await refreshBalance();
      nav(`/projects/${project.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start generation");
      setBusy(false);
    }
  };

  const applyTemplate = (t) => {
    const s = t.settings;
    setProviderId(s.provider_id || "ffmpeg-preview");
    setAutoTier(s.auto_tier || "best_value");
    setStyle(s.style); setAspect(s.aspect); setResolution(s.resolution);
    setLanguage(s.language); setMusicTrack(s.music_track || null);
    setMusicVolume(s.music_volume ?? 0.35);
    setVoiceEnabled(s.voice_enabled); setVoiceGender(s.voice_gender || "female");
    setCaptionsEnabled(s.captions_enabled); setBrandingEnabled(s.branding_enabled);
    toast.success(`Applied template: ${t.name}`);
  };

  const dropHandler = (e) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer?.files) handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="p-6 md:p-10 lg:p-14 max-w-[1400px]">
      <button onClick={() => nav("/")} data-testid="back-button"
        className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm mb-6">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to dashboard
      </button>

      <div className="mb-10">
        <div className="font-mono-tech text-[10px] text-amber-400 mb-3">CREATE · NEW · VIDEO</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light leading-none">
          Studio <span className="font-bold text-amber-400">workflow.</span>
        </h1>
      </div>

      {/* Sticky top section navigator — one-click scroll between the three
          creation zones (Setup / Photos / AI+Style) without losing any data.
          On mobile this stays inline; on desktop it becomes a sticky pill. */}
      <div
        className="sticky top-4 z-30 -mx-2 mb-8 flex items-center gap-2 rounded-full bg-[#121214]/95 backdrop-blur border border-white/5 px-2 py-1.5 max-w-max font-mono-tech text-[10px]"
        data-testid="section-navigator"
      >
        {[
          { id: "setup",  i: 1, label: "SETUP",     done: !!name.trim() },
          { id: "photos", i: 2, label: "PHOTOS",    done: (project?.photos?.length || 0) > 0 },
          { id: "style",  i: 3, label: "AI · STYLE", done: !!project },
        ].map((s, idx) => {
          const active = activeSection === s.id;
          return (
            <div key={s.id} className="flex items-center gap-2">
              <button
                onClick={() => scrollToSection(s.id)}
                data-testid={`nav-${s.id}`}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors ${
                  active
                    ? "bg-amber-500 text-black"
                    : s.done
                      ? "text-emerald-300 hover:bg-white/5"
                      : "text-zinc-400 hover:bg-white/5"
                }`}
              >
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] border ${
                  active
                    ? "border-black/40 bg-black/20 text-black font-bold"
                    : s.done
                      ? "border-emerald-500 bg-emerald-500/20 text-emerald-400"
                      : "border-white/15 text-zinc-500"
                }`}>{s.i}</span>
                <span className="uppercase tracking-wider">{s.label}</span>
              </button>
              {idx < 2 && <span className="text-zinc-700">·</span>}
            </div>
          );
        })}
      </div>

      {/* Global restore banner — visible on every section when in edit mode. */}
      {editProjectId && project && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.06] p-4 mb-8 max-w-4xl" data-testid="restore-banner">
          <div className="flex items-center gap-2 font-mono-tech text-[10px] uppercase text-emerald-300 tracking-wider mb-1">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" />
            Project restored
          </div>
          <div className="text-sm text-zinc-200 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>
              Editing <span className="font-medium">{project.name}</span> — {(project.photos || []).length} photo{(project.photos || []).length === 1 ? "" : "s"} preserved. All settings are pre-filled.
            </span>
            {activeSection !== "setup" && (
              <button
                onClick={() => scrollToSection("setup")}
                data-testid="restore-edit-info"
                className="text-[11px] font-mono-tech uppercase tracking-wider text-emerald-300 hover:text-emerald-200"
              >
                Edit project info →
              </button>
            )}
          </div>
        </div>
      )}

      {/* ============ SECTION 1 · SETUP ============ */}
      <section id="setup" ref={setupRef} className="scroll-mt-24 mb-12" data-testid="section-setup">
        <div className="font-mono-tech text-[10px] text-amber-400 mb-3 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-amber-500 text-black flex items-center justify-center text-[10px] font-bold">1</span>
          PROJECT · INFO
        </div>
        {(() => {
          // Templates surfaced only when user has some AND isn't restoring a project.
          const showTemplateCard = templates.length > 0 && !editProjectId;
          const gridCols = showTemplateCard ? "lg:grid-cols-2" : "lg:grid-cols-1";
          return (
        <div className={`grid grid-cols-1 ${gridCols} gap-8 max-w-4xl`}>
          <div className="space-y-4">
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">PROJECT NAME *</Label>
              <Input data-testid="input-name" value={name} onChange={(e) => setName(e.target.value)}
                onBlur={() => { if (name.trim()) createDraft({ silent: true, scrollNext: false }); }}
                placeholder="Villa Verde – Ibiza"
                className="mt-2 bg-[#0b0b0d] border-white/10 focus-visible:border-amber-500 h-11" />
              {project?.id && (
                <div className="mt-1.5 flex items-center gap-1.5 text-[10px] font-mono-tech text-emerald-400" data-testid="autosave-indicator">
                  <span className="w-1 h-1 rounded-full bg-emerald-400" />
                  Saved as draft — safe to keep going
                </div>
              )}
            </div>
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">ADDRESS</Label>
              <Input data-testid="input-address" value={address} onChange={(e) => setAddress(e.target.value)}
                onBlur={() => { if (project?.id) createDraft({ silent: true, scrollNext: false }); }}
                placeholder="Cala Vadella, Ibiza, Spain"
                className="mt-2 bg-[#0b0b0d] border-white/10 h-11" />
            </div>
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">DESCRIPTION</Label>
              <Textarea data-testid="input-description" value={description} onChange={(e) => setDescription(e.target.value)}
                onBlur={() => { if (project?.id) createDraft({ silent: true, scrollNext: false }); }}
                placeholder="Modern 4-bed villa with panoramic sea view..." rows={5}
                className="mt-2 bg-[#0b0b0d] border-white/10" />
            </div>
          </div>
          {showTemplateCard && (
            <div className="bg-[#121214] border border-white/5 rounded-lg p-6" data-testid="templates-card">
              <div className="flex items-center justify-between mb-4">
                <div className="font-mono-tech text-[10px] text-zinc-500">
                  START FROM · TEMPLATE ({templates.length})
                </div>
                <Link
                  to="/templates"
                  data-testid="templates-manage-link"
                  className="text-[10px] font-mono-tech uppercase tracking-wider text-amber-400 hover:text-amber-300"
                >
                  Manage →
                </Link>
              </div>
              <div className="space-y-2">
                {templates.slice(0, 6).map((t) => (
                  <button key={t.id} onClick={() => applyTemplate(t)}
                    data-testid={`template-quickapply-${t.id}`}
                    className="w-full text-left px-3 py-2 rounded border border-white/5 hover:border-amber-500/50 hover:bg-amber-500/5 transition-colors">
                    <div className="text-sm font-medium">{t.name}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5 uppercase tracking-wider">
                      {t.settings.provider_id} · {t.settings.aspect}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className={`${showTemplateCard ? "lg:col-span-2" : ""} flex justify-end`}>
            <Button data-testid="setup-continue" disabled={busy || !name.trim()}
              onClick={async () => {
                const saved = await createDraft({ silent: false, scrollNext: true });
                if (saved) scrollToSection("photos");
              }}
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold h-11 px-6">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Continue to photos →
            </Button>
          </div>
        </div>
          );
        })()}
      </section>

      {/* ============ SECTION 2 · PHOTOS ============ */}
      <section id="photos" ref={photosRef} className="scroll-mt-24 mb-12" data-testid="section-photos">
        <div className="font-mono-tech text-[10px] text-amber-400 mb-3 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-amber-500 text-black flex items-center justify-center text-[10px] font-bold">2</span>
          PHOTOS · UPLOAD
        </div>
        {!project ? (
          <div className="rounded-lg border border-dashed border-white/10 bg-[#0f0f11]/50 p-8 text-center max-w-4xl" data-testid="photos-locked">
            <Lock className="w-6 h-6 text-zinc-500 mx-auto mb-2" strokeWidth={1.5} />
            <div className="text-sm text-zinc-400">
              Give your project a name above — photos will unlock automatically.
            </div>
          </div>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={dropHandler}
              onClick={() => fileRef.current?.click()}
              data-testid="dropzone"
              className={`cursor-pointer rounded-xl p-14 text-center transition-colors ${
                dragOver ? "co-dashed-active bg-amber-500/5" : "co-dashed bg-white/[0.02] hover:bg-white/[0.04]"
              }`}>
              <input ref={fileRef} type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                className="hidden" data-testid="file-input"
                onChange={(e) => handleFiles(Array.from(e.target.files || []))} />
              <ImagePlus className="w-10 h-10 text-amber-400 mx-auto mb-4" strokeWidth={1.5} />
              <div className="font-heading text-xl font-bold tracking-tight">Drop photos here or click to browse</div>
              <div className="text-zinc-500 text-sm mt-2">
                JPG · PNG · WebP · up to 30 photos · {project.photos?.length || 0} added
              </div>
            </div>
            {project.photos?.length > 0 && (
              <div className="mt-8">
                <div className="font-mono-tech text-[10px] text-zinc-500 mb-4">
                  {project.photos.length} · PHOTOS · TAG · SCENE · FOR · BEST · MOTION
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  <AnimatePresence>
                    {project.photos.map((ph) => (
                      <motion.div key={ph.id}
                        initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="relative group bg-[#121214] border border-white/5 rounded-lg overflow-hidden"
                        data-testid={`photo-${ph.id}`}>
                        <img src={fileUrl(`photos/${project.id}/${ph.filename}`)} alt=""
                          onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement?.classList.add("photo-missing"); }}
                          className="w-full aspect-square object-cover" />
                        <div className="absolute inset-0 flex items-center justify-center bg-[#0b0b0d] pointer-events-none opacity-0 [.photo-missing_&]:opacity-100">
                          <div className="text-center px-3">
                            <div className="font-mono-tech text-[9px] text-zinc-500 uppercase">Photo · missing</div>
                            <div className="text-[10px] text-zinc-600 mt-1 truncate">Re-upload to fix</div>
                          </div>
                        </div>
                        <button onClick={() => removePhoto(ph.id)}
                          data-testid={`remove-photo-${ph.id}`}
                          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 backdrop-blur flex items-center justify-center text-white opacity-0 group-hover:opacity-100 hover:bg-red-500 transition-all">
                          <X className="w-3.5 h-3.5" strokeWidth={2} />
                        </button>
                        <div className="p-2 border-t border-white/5">
                          <Select value={ph.scene || "auto"}
                            onValueChange={(v) => setScene(ph.id, v === "auto" ? "" : v)}>
                            <SelectTrigger data-testid={`scene-${ph.id}`}
                              className="h-7 bg-transparent border-white/10 text-[11px] font-mono-tech uppercase tracking-wider">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1a1a1e] border-white/10">
                              <SelectItem value="auto">Auto</SelectItem>
                              {catalog?.scenes.map((s) => (
                                <SelectItem key={s} value={s}>{SCENE_LABEL[s] || s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            )}
          </div>
          <div className="lg:col-span-4">
            <div className="sticky top-24 bg-[#121214] border border-white/5 rounded-lg p-6">
              <div className="font-mono-tech text-[10px] text-zinc-500 mb-4">TIPS</div>
              <ul className="space-y-3 text-sm text-zinc-300">
                <li className="flex gap-2"><span className="text-amber-400">·</span> 5–10 photos give the best cinematic flow.</li>
                <li className="flex gap-2"><span className="text-amber-400">·</span> AI providers turn each photo into a moving clip with realistic camera motion.</li>
                <li className="flex gap-2"><span className="text-amber-400">·</span> Tag scenes (pool, garden, view) — the AI matches the right motion.</li>
              </ul>
              <div className="flex gap-2 mt-6">
                <Button variant="outline" onClick={() => scrollToSection("setup")}
                  className="flex-1 bg-transparent border-white/15">Back</Button>
                <Button onClick={() => scrollToSection("style")} disabled={!project.photos?.length}
                  data-testid="photos-continue"
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-semibold">
                  Continue
                </Button>
              </div>
            </div>
          </div>
        </div>
        )}
      </section>

      {/* ============ SECTION 3 · AI · STYLE ============ */}
      <section id="style" ref={styleRef} className="scroll-mt-24" data-testid="section-style">
        <div className="font-mono-tech text-[10px] text-amber-400 mb-3 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-amber-500 text-black flex items-center justify-center text-[10px] font-bold">3</span>
          AI · STYLE · GENERATE
        </div>
        {!project || !catalog ? (
          <div className="rounded-lg border border-dashed border-white/10 bg-[#0f0f11]/50 p-8 text-center max-w-4xl" data-testid="style-locked">
            <Lock className="w-6 h-6 text-zinc-500 mx-auto mb-2" strokeWidth={1.5} />
            <div className="text-sm text-zinc-400">
              {!project ? "Create the project first (Section 1)." : "Loading style options…"}
            </div>
          </div>
        ) : (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
          <div className="xl:col-span-8 space-y-8">

            {/* Plan limits banner */}
            {currentPlan && !isOwner && (
              <PlanLimitsBanner plan={currentPlan.plan} numPhotos={project?.photos?.length || 0} />
            )}

            {/* AI ENGINE — Simplified 4-tier picker */}
            <section>
              <div className="flex items-baseline justify-between mb-3">
                <div className="font-mono-tech text-[10px] text-zinc-500">AI · ENGINE</div>
                <button
                  onClick={() => setAdvancedMode((v) => !v)}
                  data-testid="advanced-mode-toggle"
                  className="text-[11px] font-mono-tech text-amber-400 hover:text-amber-300"
                >
                  {advancedMode ? "← Simple picker" : "Advanced mode →"}
                </button>
              </div>

              {!advancedMode ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {/* Smart AI */}
                    <button
                      onClick={() => { setProviderId("auto"); setAutoTier("best_value"); }}
                      data-testid="tier-smart"
                      className={`relative p-5 rounded-lg border text-left transition-colors ${
                        providerId === "auto"
                          ? "border-amber-500 bg-gradient-to-br from-amber-500/15 to-amber-500/5"
                          : "border-white/5 bg-[#121214] hover:border-white/15"
                      }`}
                    >
                      <div className="absolute top-2 right-2 font-mono-tech text-[8px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 uppercase">
                        Recommended
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-amber-400" strokeWidth={1.75} />
                        <div className="font-heading font-bold text-base tracking-tight">Smart AI</div>
                        <span className="text-[10px] text-amber-400">⭐⭐⭐⭐</span>
                      </div>
                      <div className="text-xs text-zinc-400 mb-3">
                        Automatically picks the best engine — quality, speed and availability balanced.
                      </div>
                      <div className="flex items-center gap-1 text-[11px] text-zinc-400 font-mono-tech">
                        <Coins className="w-3 h-3 text-amber-400" strokeWidth={1.5} />
                        20-80 credits/clip
                      </div>
                    </button>

                    {/* Budget */}
                    <TierCard
                      id="budget"
                      label="Budget"
                      stars="⭐"
                      credits={tierInfo?.tiers?.find((t) => t.tier_group === "budget")?.credits_per_clip ?? 20}
                      providersHint={(tierInfo?.tiers?.find((t) => t.tier_group === "budget")?.provider_names || []).join(" · ")}
                      description="Fast, great quality — perfect for daily social content."
                      selected={providerId === "budget-tier"}
                      onSelect={() => { setProviderId("budget-tier"); setAutoTier("cheapest"); }}
                    />

                    {/* Premium */}
                    <TierCard
                      id="premium"
                      label="Premium"
                      stars="⭐⭐"
                      credits={tierInfo?.tiers?.find((t) => t.tier_group === "premium")?.credits_per_clip ?? 35}
                      providersHint={(tierInfo?.tiers?.find((t) => t.tier_group === "premium")?.provider_names || []).join(" · ")}
                      description="Cinematic quality — the sweet spot for pro creators."
                      selected={providerId === "premium-tier"}
                      onSelect={() => { setProviderId("premium-tier"); setAutoTier("best_value"); }}
                      highlight
                    />

                    {/* Ultra */}
                    <TierCard
                      id="ultra"
                      label="Ultra"
                      stars="⭐⭐⭐"
                      credits={tierInfo?.tiers?.find((t) => t.tier_group === "ultra")?.credits_per_clip ?? 80}
                      providersHint={(tierInfo?.tiers?.find((t) => t.tier_group === "ultra")?.provider_names || []).join(" · ")}
                      description="Absolute top-tier — hyperrealistic motion for hero content."
                      selected={providerId === "ultra-tier"}
                      onSelect={() => { setProviderId("ultra-tier"); setAutoTier("best_quality"); }}
                    />
                  </div>

                  {/* Preview option — free */}
                  <button
                    onClick={() => setProviderId("ffmpeg-preview")}
                    data-testid="tier-preview"
                    className={`w-full mt-3 p-3 rounded-lg border text-left transition-colors flex items-center justify-between ${
                      providerId === "ffmpeg-preview"
                        ? "border-emerald-500 bg-emerald-500/10"
                        : "border-white/5 bg-[#121214] hover:border-white/15"
                    }`}
                  >
                    <div>
                      <div className="font-heading font-bold text-sm tracking-tight flex items-center gap-2">
                        Free Preview
                        <span className="font-mono-tech text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 uppercase">
                          0 credits
                        </span>
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">
                        Ken-Burns motion — perfect to test your storyboard before spending credits.
                      </div>
                    </div>
                  </button>
                </>
              ) : (
                <>
                  <div className="text-xs text-zinc-500 mb-4">
                    Advanced: pick a specific engine. Prices are fixed per tier.
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {providers.map((p) => {
                      const active = providerId === p.id;
                      const needsKey = !!p.requires_key;
                      const locked = needsKey && !hasFalKey;
                      const tierBadgeCls = {
                        budget:  "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
                        premium: "bg-amber-500/20 text-amber-300 border-amber-500/30",
                        ultra:   "bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30",
                        preview: "bg-white/10 text-zinc-300 border-white/10",
                      }[p.tier_group || "budget"];
                      return (
                        <button key={p.id}
                          onClick={() => !locked && setProviderId(p.id)}
                          disabled={locked}
                          data-testid={`provider-${p.id}`}
                          title={locked ? "Requires fal.ai key" : ""}
                          className={`p-4 rounded-lg border text-left transition-colors relative ${
                            active ? "border-amber-500 bg-amber-500/10" :
                              locked ? "border-white/5 bg-[#0f0f11] opacity-50 cursor-not-allowed" :
                              "border-white/5 bg-[#121214] hover:border-white/15"
                          }`}>
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div className="font-heading font-bold text-sm tracking-tight">{p.name}</div>
                            {locked && <Lock className="w-3.5 h-3.5 text-zinc-500" strokeWidth={1.5} />}
                          </div>
                          <div className="text-[10px] text-zinc-500 mb-3 line-clamp-2">{p.description}</div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={`text-[9px] font-mono-tech uppercase ${tierBadgeCls}`}>
                              {p.tier_group || "budget"}
                            </Badge>
                            <div className="flex items-center gap-1 text-[10px] text-zinc-400 font-mono-tech">
                              <Coins className="w-3 h-3 text-amber-400" strokeWidth={1.5} />
                              {p.credits_per_clip}/clip
                            </div>
                            {p.credits_per_clip === 0 && (
                              <span className="text-[9px] font-mono-tech text-emerald-400">FREE</span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </section>

            {/* Style */}
            <section>
              <div className="font-mono-tech text-[10px] text-zinc-500 mb-3">STYLE · LOOK</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {catalog.styles.map((s) => (
                  <button key={s.id} onClick={() => setStyle(s.id)}
                    data-testid={`style-${s.id}`}
                    className={`text-left p-4 rounded-lg border transition-colors ${
                      style === s.id ? "border-amber-500 bg-amber-500/10"
                        : "border-white/5 bg-[#121214] hover:border-white/15"}`}>
                    <div className="font-heading font-bold tracking-tight">{s.name}</div>
                  </button>
                ))}
              </div>
            </section>

            {/* Aspect + Resolution */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <div className="font-mono-tech text-[10px] text-zinc-500 mb-3">ASPECT RATIO</div>
                <div className="grid grid-cols-3 gap-3">
                  {catalog.aspects.map((a) => {
                    const iconClass = a.id === "16:9" ? "w-14 h-8" : a.id === "9:16" ? "w-8 h-14" : "w-10 h-10";
                    return (
                      <button key={a.id} onClick={() => setAspect(a.id)}
                        data-testid={`aspect-${a.id}`}
                        className={`flex flex-col items-center gap-2 p-4 rounded-lg border transition-colors ${
                          aspect === a.id ? "border-amber-500 bg-amber-500/10"
                            : "border-white/5 bg-[#121214] hover:border-white/15"}`}>
                        <div className={`${iconClass} border-2 ${aspect === a.id ? "border-amber-400" : "border-white/40"} rounded`} />
                        <div className="font-mono-tech text-[10px]">{a.id}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="font-mono-tech text-[10px] text-zinc-500 mb-3">RESOLUTION</div>
                <div className="grid grid-cols-2 gap-3">
                  {catalog.resolutions.map((r) => {
                    const locked = !isResolutionAllowed(r.id);
                    return (
                      <button key={r.id} onClick={() => !locked && setResolution(r.id)}
                        disabled={locked}
                        data-testid={`resolution-${r.id}`}
                        title={locked ? "Upgrade your plan to unlock this resolution" : ""}
                        className={`p-4 rounded-lg border transition-colors relative ${
                          resolution === r.id ? "border-amber-500 bg-amber-500/10" :
                          locked ? "border-white/5 bg-[#0f0f11] opacity-50 cursor-not-allowed" :
                          "border-white/5 bg-[#121214] hover:border-white/15"}`}>
                        {locked && (
                          <div className="absolute top-1.5 right-1.5 font-mono-tech text-[8px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 uppercase">
                            Upgrade
                          </div>
                        )}
                        <div className="font-heading font-bold tracking-tight">{r.name}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Music */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono-tech text-[10px] text-zinc-500">MUSIC</div>
                <label className="text-xs text-amber-400 hover:text-amber-300 cursor-pointer font-medium">
                  <input type="file" accept="audio/*" className="hidden" data-testid="upload-music"
                    onChange={async (e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      const fd = new FormData(); fd.append("file", f);
                      try {
                        const { data } = await api.post("/music", fd);
                        const r = await api.get("/music"); setTracks(r.data);
                        setMusicTrack(data.filename); toast.success("Music uploaded");
                      } catch { toast.error("Upload failed"); }
                    }} />
                  + Upload your own
                </label>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <button onClick={() => setMusicTrack(null)} data-testid="music-none"
                  className={`p-3 rounded-lg border text-left transition-colors ${
                    !musicTrack ? "border-amber-500 bg-amber-500/10"
                      : "border-white/5 bg-[#121214] hover:border-white/15"}`}>
                  <div className="text-sm font-medium">No music</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">Voice-over or silence only</div>
                </button>
                {tracks.map((t) => (
                  <button key={t.filename} onClick={() => setMusicTrack(t.filename)}
                    data-testid={`music-${t.filename}`}
                    className={`p-3 rounded-lg border text-left transition-colors flex items-start gap-2 ${
                      musicTrack === t.filename ? "border-amber-500 bg-amber-500/10"
                        : "border-white/5 bg-[#121214] hover:border-white/15"}`}>
                    <Music2 className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{t.name}</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">{(t.size / 1024).toFixed(0)} KB</div>
                    </div>
                  </button>
                ))}
              </div>
              {musicTrack && (
                <div className="mt-4">
                  <Label className="font-mono-tech text-[10px] text-zinc-500">
                    MUSIC VOLUME · {Math.round(musicVolume * 100)}%
                  </Label>
                  <Slider value={[musicVolume * 100]} onValueChange={(v) => setMusicVolume(v[0] / 100)}
                    max={100} step={5} className="mt-3" data-testid="music-volume" />
                </div>
              )}
            </section>

            {/* Voice-over */}
            <section className="bg-[#121214] border border-white/5 rounded-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-heading font-bold tracking-tight">AI Voice-over</div>
                  <div className="text-xs text-zinc-500 mt-0.5">Free multilingual neural voice</div>
                </div>
                <Switch checked={voiceEnabled} onCheckedChange={setVoiceEnabled} data-testid="voice-toggle" />
              </div>
              {voiceEnabled && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="font-mono-tech text-[10px] text-zinc-500">LANGUAGE</Label>
                      <Select value={language} onValueChange={setLanguage}>
                        <SelectTrigger data-testid="voice-lang" className="mt-2 bg-[#0b0b0d] border-white/10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1a1e] border-white/10">
                          {catalog.languages.map((l) => (<SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="font-mono-tech text-[10px] text-zinc-500">VOICE</Label>
                      <Select value={voiceGender} onValueChange={setVoiceGender}>
                        <SelectTrigger data-testid="voice-gender" className="mt-2 bg-[#0b0b0d] border-white/10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1a1e] border-white/10">
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="male">Male</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="font-mono-tech text-[10px] text-zinc-500">VOICE SCRIPT</Label>
                    <Textarea value={voiceScript} onChange={(e) => setVoiceScript(e.target.value)} rows={4}
                      placeholder="Discover this stunning modern villa nestled in the hills of..."
                      className="mt-2 bg-[#0b0b0d] border-white/10" data-testid="voice-script" />
                  </div>
                </div>
              )}
            </section>

            {/* Captions + branding */}
            <section className="bg-[#121214] border border-white/5 rounded-lg p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-heading font-bold tracking-tight">On-screen caption</div>
                  <div className="text-xs text-zinc-500 mt-0.5">Bottom-left text overlay</div>
                </div>
                <Switch checked={captionsEnabled} onCheckedChange={setCaptionsEnabled} data-testid="captions-toggle" />
              </div>
              {captionsEnabled && (
                <Input value={captionText} onChange={(e) => setCaptionText(e.target.value)}
                  placeholder="Villa Verde · 4 bedrooms · Ibiza" data-testid="caption-text"
                  className="bg-[#0b0b0d] border-white/10" />
              )}
              <div className="flex items-center justify-between pt-3 border-t border-white/5">
                <div>
                  <div className="font-heading font-bold tracking-tight">Branding overlay</div>
                  <div className="text-xs text-zinc-500 mt-0.5">Logo + website watermark</div>
                </div>
                <Switch checked={brandingEnabled} onCheckedChange={setBrandingEnabled} data-testid="branding-toggle" />
              </div>
            </section>
          </div>

          {/* Right rail — Review + cost */}
          <div className="xl:col-span-4">
            <div className="sticky top-6 space-y-4">
              <div className="bg-[#121214] border border-white/5 rounded-lg p-5">
                <div className="font-mono-tech text-[10px] text-zinc-500 mb-3">REVIEW</div>
                <div className="space-y-2 text-sm">
                  <Row label="Photos" value={project.photos?.length || 0} />
                  <Row label="Provider" value={effectiveProvider?.name || providerId} />
                  <Row label="Style" value={style} />
                  <Row label="Aspect" value={aspect} />
                  <Row label="Resolution" value={resolution.toUpperCase()} />
                  <Row label="Music" value={musicTrack ? "Yes" : "No"} />
                  <Row label="Voice-over" value={voiceEnabled ? language.toUpperCase() : "Off"} />
                </div>
              </div>

              {/* Upsell prompt — non-owner, non-Studio+ who repeatedly try Premium/Ultra */}
              {showUpsell && (
                <Link
                  to="/pricing"
                  data-testid="upsell-studio-banner"
                  className="block rounded-lg p-4 border border-amber-500/40 bg-gradient-to-br from-amber-500/[0.12] to-amber-500/[0.04] hover:from-amber-500/[0.18] hover:to-amber-500/[0.08] transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-md bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-4 h-4 text-amber-400" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono-tech text-[9px] text-amber-400 uppercase tracking-wider mb-0.5">
                        Studio · Unlock
                      </div>
                      <div className="text-sm font-medium text-white leading-tight">
                        Go premium without watching the credit meter.
                      </div>
                      <div className="text-[11px] text-zinc-400 mt-1.5 leading-relaxed">
                        Studio subscribers get monthly credits, priority rendering and AI Brand Kit — enough headroom for every {tierGroup} render.
                      </div>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-amber-400 flex-shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
                  </div>
                </Link>
              )}

              {/* Credit cost card */}
              <div className={`rounded-lg p-5 border ${
                canAfford ? "bg-amber-500/10 border-amber-500/30" : "bg-red-500/10 border-red-500/30"
              }`}>
                <div className="font-mono-tech text-[10px] mb-2 text-amber-400">TOTAL · COST</div>
                <div className="flex items-baseline gap-2">
                  <div className="font-heading text-4xl font-bold tracking-tighter text-white">
                    {totalCredits}
                  </div>
                  <div className="text-xs text-zinc-400">credits</div>
                </div>
                <div className="text-[11px] text-zinc-400 mt-2">
                  {project.photos?.length || 0} photos × {effectiveProvider?.credits_per_clip || 0} credits/clip
                </div>
                <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-xs">
                  <span className="text-zinc-500">Your balance</span>
                  <span className={canAfford ? "text-white font-medium" : "text-red-400 font-medium"}>
                    {isOwner || balance.unlimited ? "∞" : balance.balance} credits
                  </span>
                </div>
                {!canAfford && (
                  <div className="mt-3 text-xs text-red-300">
                    Not enough credits. <a href="/pricing" className="underline">Buy more →</a>
                  </div>
                )}
              </div>

              <Button onClick={saveAndGenerate} disabled={busy || !canAfford || !planOk} data-testid="generate-video"
                className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-black font-bold text-base disabled:opacity-40">
                {busy ? <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  : <Rocket className="w-5 h-5 mr-2" strokeWidth={2} />}
                Generate video
              </Button>

              {!planOk && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/[0.06] p-3 space-y-1.5" data-testid="plan-violations">
                  <div className="flex items-center gap-2 text-red-300 text-xs font-mono-tech uppercase">
                    <TriangleAlert className="w-3.5 h-3.5" strokeWidth={2} />
                    Plan limits
                  </div>
                  {planViolations.map((v, i) => (
                    <div key={i} className="text-xs text-red-200 pl-5">• {v}</div>
                  ))}
                  <Link to="/pricing" className="inline-block mt-1 text-amber-400 text-xs font-medium hover:underline pl-5">
                    Upgrade your plan →
                  </Link>
                </div>
              )}

              <Button variant="outline"
                onClick={async () => {
                  const name_ = prompt("Save current settings as template. Name?");
                  if (!name_) return;
                  await api.post("/templates", { name: name_, settings: currentSettings() });
                  const r = await api.get("/templates"); setTemplates(r.data);
                  toast.success("Template saved");
                }}
                data-testid="save-template"
                className="w-full bg-transparent border-white/15 hover:bg-white/5">
                <Wand2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> Save as template
              </Button>

              <Button variant="ghost" onClick={() => scrollToSection("photos")}
                className="w-full text-zinc-400 hover:text-white hover:bg-white/5">
                Back to photos
              </Button>
            </div>
          </div>
        </div>
        )}
      </section>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-white/5 pb-2 last:border-0">
      <span className="font-mono-tech text-[10px] text-zinc-500">{label.toUpperCase()}</span>
      <span className="text-white font-medium capitalize">{String(value)}</span>
    </div>
  );
}

// ---------- Plan Limits Banner ----------
function PlanLimitsBanner({ plan, numPhotos }) {
  const DEFAULT_DUR = 5; // matches backend default duration_per_photo
  const totalLen = numPhotos * DEFAULT_DUR;
  const maxLen = plan?.max_length_seconds || 999;
  const exceeds = totalLen > maxLen;
  const maxPhotos = Math.floor(maxLen / DEFAULT_DUR);

  return (
    <section className={`rounded-lg border p-4 flex items-start gap-3 ${
      exceeds ? "border-red-500/30 bg-red-500/[0.06]" : "border-white/5 bg-[#121214]"
    }`} data-testid="plan-limits-banner">
      {exceeds ? (
        <TriangleAlert className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
      ) : (
        <Sparkles className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <div className="font-mono-tech text-[10px] text-amber-400">YOUR · {plan?.name?.toUpperCase()} · PLAN</div>
          {plan?.trial_days > 0 && (
            <span className="font-mono-tech text-[9px] px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded uppercase">
              {plan.trial_days}d trial available
            </span>
          )}
        </div>
        {exceeds ? (
          <div className="text-sm text-red-200">
            <b>Your {plan.name} plan allows videos up to {maxLen}s.</b> With {numPhotos} photos × {DEFAULT_DUR}s = <b>{totalLen}s</b>.
            {maxPhotos > 0 && (
              <span> Remove {numPhotos - maxPhotos} photo{numPhotos - maxPhotos > 1 ? "s" : ""} to fit, or upgrade below.</span>
            )}
          </div>
        ) : (
          <div className="text-xs text-zinc-400">
            Max {maxLen}s per video · {(plan?.allowed_resolutions || []).join("/").toUpperCase() || "1080p+4K"} ·
            {plan?.allowed_providers?.length ? ` ${plan.allowed_providers.length} provider${plan.allowed_providers.length > 1 ? "s" : ""}` : " all providers"}
            {plan?.watermark ? " · watermark on" : ""}
          </div>
        )}
      </div>
      {exceeds && (
        <Link to="/pricing" data-testid="banner-upgrade-cta">
          <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-black font-semibold whitespace-nowrap">
            <ArrowUpRight className="w-4 h-4 mr-1" strokeWidth={1.5} />
            Upgrade
          </Button>
        </Link>
      )}
    </section>
  );
}

